import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { getIn, useFormikContext } from 'formik';
import { useStore } from 'core/store/store.mobx';
import Grid from '@material-ui/core/Grid';

import useSession from 'core/hooks/use-session';
import CollapsibleCard from 'core/components/collapsible-card';
import { Label } from 'core/components/label';
import Section from 'core/components/section';
import RepeatField from 'core/components/repeat-field';
import { useHideEditControls } from 'common/disabled-context';

function FormList({
  compact = false,
  item: Item,
  addForm: AddForm,
  addLabel,
  title,
  disabled,
  id,
  secondaryId,
  singular,
  plural,
  children,
  type,
  classes,
  hasAuto,
  fromPolicy,
  inOffer,
  sppLimits,
  sectionTooltip,
  onAdd,
  showRightLabel,
  notification,
  maxSize = 40,
  ...rest
}) {
  const [folded, setFolded] = useState([]);
  const [indexToRemove, setIndexToRemove] = useState(-1);
  const { values, errors, setFieldValue, setFieldTouched } = useFormikContext();
  const list = useMemo(() => {
    if (secondaryId) {
      // allow a secondary id so we can display items from multiple lists, mostly useful for PIP when we need to show drivers and nonDrivers
      return (getIn(values, secondaryId) ?? []).concat(getIn(values, id) ?? []);
    }
    return getIn(values, id) ?? [];
  }, [values, id, secondaryId]);

  const toggleFold = (i, foldedStatus) =>
    setFolded((prevFolded) => [...prevFolded.slice(0, i), foldedStatus, ...prevFolded.slice(i + 1, prevFolded.length)]);

  const {
    offer: offerStore,
    account: {
      policies: { policy: policyStore }
    }
  } = useStore();

  // When the Validation Errors modal sends the user to this list (scrollTo === id), open every item
  // that has a validation error so the offending field is actually visible after the scroll.
  //
  // We can't use a boolean forceOpen: CollapsibleCard force-CLOSES on a true->false change (would
  // slam an open card shut), and a latched-true value never CHANGES so it can't reopen a card the
  // user collapsed between two navigations. Instead bump a per-index openSignal counter each time we
  // become the scroll target; CollapsibleCard's openSignal is open-only — a changed value opens, and
  // it never closes — so repeated navigations reliably reopen, and manual collapses aren't fought.
  // (#30230 go-to-section.)
  const scrollTo = offerStore?.scrollTo || policyStore?.scrollTo;
  const [openSignals, setOpenSignals] = useState([]);
  useEffect(() => {
    if (scrollTo !== id) {
      return;
    }
    const listErrors = getIn(errors, id);
    if (!Array.isArray(listErrors)) {
      return;
    }
    // Increment the signal for each item that has an error; leave the rest untouched.
    setOpenSignals((prev) => listErrors.map((itemError, i) => (itemError ? (prev[i] || 0) + 1 : prev[i] || 0)));
  }, [scrollTo, id, errors]);

  const session = useSession();
  const hideEditControls = useHideEditControls();

  const allowLicensedActions = fromPolicy
    ? policyStore?.getAllowLicensedActions(session)
    : offerStore?.getIsLicensedForState(session);

  const pushItem = (val) => {
    const item = val;

    Object.keys(item).forEach((key) => {
      if (item[key] === '') {
        item[key] = null;
      }
    });

    // if we have a secondary id, we need to ensure we are only adding the new item to the first id/node, since the list contains both the id and the secondary id values
    const listToUpload = secondaryId ? (values[id] ?? []) : list;

    setFieldValue(id, [...listToUpload, item]);
    setFieldTouched(id);

    if (onAdd) {
      onAdd(item);
    }
  };

  useEffect(() => {
    if (indexToRemove !== -1) {
      const newList = list.slice();
      newList.splice(indexToRemove, 1);
      setFolded([...folded.slice(0, indexToRemove), ...folded.slice(indexToRemove + 1)]);
      setFieldValue(id, newList);
      setFieldTouched(id);
      setIndexToRemove(-1);
    }
  }, [list, indexToRemove, folded, setFieldValue, setFieldTouched, id]);

  return (
    <Section
      className={classes.container}
      title={title}
      type={type || 'SubSection'}
      rightLabel="Total"
      rightValue={`${list.length} ${list.length === 1 ? singular : plural}`}
      id={id}
      titleTooltipText={sectionTooltip}
      showRightLabel={showRightLabel}
    >
      <Grid className={classes.innerContainer}>
        {children}
        <RepeatField
          initialLength={list.length}
          key={list.length}
          maxSize={maxSize}
          notification={notification}
          control={() => {
            if (hideEditControls) {
              return null;
            }
            const form = (
              <AddForm
                id={id}
                disabled={disabled}
                onAdd={pushItem}
                label={addLabel}
                currentPeopleOrDrivers={list}
                listLength={list.length}
                inOffer={inOffer}
                sppLimits={sppLimits}
                {...rest}
              />
            );
            return compact ? (
              <CollapsibleCard content={<Label type="body1">{addLabel || `Add ${singular}`}</Label>}>
                {form}
              </CollapsibleCard>
            ) : (
              form
            );
          }}
        >
          {({ onRemove, focus }, i) => {
            const p = list[i];
            return (
              <Item
                fieldName={`${id}[${i}]`}
                fieldId={id}
                key={p?.id || i}
                index={i}
                item={p}
                onRemove={setIndexToRemove}
                removeFromList={onRemove}
                focus={focus}
                initialFold={!!folded[i]}
                openSignalOnError={openSignals[i] || 0}
                onFold={toggleFold}
                disabled={disabled}
                previous={list[i - 1]}
                count={list.length}
                allowLicensedActions={allowLicensedActions}
                hasAuto={hasAuto}
                fromPolicy={fromPolicy}
                inOffer={inOffer}
                listLength={list.length}
                {...rest}
              />
            );
          }}
        </RepeatField>
      </Grid>
    </Section>
  );
}

FormList.propTypes = {
  compact: PropTypes.bool,
  id: PropTypes.string.isRequired,
  title: PropTypes.string,
  item: PropTypes.oneOfType([PropTypes.func, PropTypes.object]).isRequired,
  addForm: PropTypes.oneOfType([PropTypes.func, PropTypes.object]).isRequired,
  addLabel: PropTypes.string,
  disabled: PropTypes.bool,
  singular: PropTypes.string,
  plural: PropTypes.string,
  children: PropTypes.node,
  type: PropTypes.string,
  classes: PropTypes.object,
  hasAuto: PropTypes.bool,
  fromPolicy: PropTypes.bool,
  inOffer: PropTypes.bool,
  sppLimits: PropTypes.object,
  sectionTooltip: PropTypes.string,
  maxSize: PropTypes.number,
  onAdd: PropTypes.func
};

FormList.defaultProps = {
  title: 'People',
  disabled: false,
  singular: 'person',
  plural: 'people',
  addLabel: undefined,
  children: undefined,
  classes: {},
  type: 'SubSection',
  hasAuto: false,
  sectionTooltip: undefined
};

export default observer(FormList);
